import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Lock, CheckCircle2, AlertCircle } from 'lucide-react';
import tapstarLogo from '@/img/tapstar_logo.png';
import { useToast } from '@/components/ui/use-toast';
import { authService } from '@/api/authService';
import { useLanguage } from '@/components/LanguageContext';
import { apiMessage } from '@/api/apiMessage';

const PasswordResetConfirm = () => {
    const { t } = useLanguage();
    const { uid, token } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg('');

        if (newPassword !== confirmPassword) {
            setErrorMsg(t('passwords_do_not_match', 'Las contraseñas no coinciden.'));
            return;
        }

        if (newPassword.length < 8) {
            setErrorMsg(t('password_too_short', 'La contraseña debe tener al menos 8 caracteres.'));
            return;
        }

        setIsLoading(true);
        try {
            await authService.confirmPasswordReset(uid, token, newPassword);
            setIsSuccess(true);
            toast({
                title: t('success_label', 'Éxito'),
                description: t('password_reset_success', 'Tu contraseña ha sido cambiada exitosamente.'),
            });
            setTimeout(() => navigate('/login'), 3000);
        } catch (error) {
            setErrorMsg(apiMessage(error, t, t('unexpected_error_reset', 'Ocurrió un error. El enlace puede ser inválido o haber expirado.')));
        } finally {
            setIsLoading(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4">
                <Card className="w-full max-w-md border border-slate-200 shadow-2xl bg-white rounded-[2rem] overflow-hidden p-10 text-center space-y-6">
                    <div className="flex justify-center">
                        <div className="h-20 w-20 bg-emerald-50 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <CardTitle className="text-2xl font-bold text-slate-900">{t('password_changed_title', 'Contraseña Actualizada')}</CardTitle>
                        <CardDescription className="text-slate-500 text-base">
                            {t('password_changed_desc', 'Tu contraseña ha sido cambiada correctamente. Serás redirigido al inicio de sesión.')}
                        </CardDescription>
                    </div>
                    <Button asChild className="w-full h-14 bg-[#feae2c] hover:bg-[#e69a1a] text-white font-bold rounded-2xl transition-all mt-4">
                        <Link to="/login">{t('back_to_login_btn', 'Ir a Iniciar Sesión')}</Link>
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4">
            <Card className="w-full max-w-md border border-slate-200 shadow-2xl bg-white rounded-[2rem] overflow-hidden">
                <CardHeader className="space-y-4 pt-10 pb-6 text-center">
                    <div className="flex justify-center mb-2">
                        <img src={tapstarLogo} alt="Tapstar Logo" className="h-16 w-auto object-contain" />
                    </div>
                    <div className="space-y-2">
                        <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">{t('create_new_password', 'Nueva Contraseña')}</CardTitle>
                        <CardDescription className="text-slate-500 text-sm">
                            {t('enter_new_password_desc', 'Por favor ingresa tu nueva contraseña a continuación.')}
                        </CardDescription>
                    </div>
                </CardHeader>

                <CardContent className="px-8 pb-8">
                    {errorMsg && (
                        <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-600 text-sm flex items-start gap-2 border border-red-100">
                            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                            <p>{errorMsg}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="newPassword" className="text-[10px] font-bold text-slate-900 flex items-center gap-1 uppercase tracking-wider">
                                <Lock className="h-3 w-3" /> {t('new_password_label', 'Nueva Contraseña')}
                            </Label>
                            <Input
                                id="newPassword"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="h-14 rounded-2xl border-slate-200 bg-white px-4 transition-all"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword" className="text-[10px] font-bold text-slate-900 flex items-center gap-1 uppercase tracking-wider">
                                <Lock className="h-3 w-3" /> {t('confirm_password_label', 'Confirmar Contraseña')}
                            </Label>
                            <Input
                                id="confirmPassword"
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="h-14 rounded-2xl border-slate-200 bg-white px-4 transition-all"
                                required
                            />
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-14 bg-[#feae2c] hover:bg-[#e69a1a] text-white font-bold rounded-2xl shadow-md transition-all uppercase tracking-wide text-sm flex items-center justify-center gap-2"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                t('save_password_btn', 'Guardar Contraseña')
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default PasswordResetConfirm;
